import { hasActiveSession } from './authCheck';
import { debugAuthentication, emergencyAuthFix } from './authDebug';

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

/**
 * Enhanced authentication checker with auto-recovery capabilities
 * This is designed to handle production authentication issues robustly
 */
export class AuthRecovery {
  private static lastCheck: number = 0;
  private static lastResult: { success: boolean; userId: string | null } | null = null;
  private static readonly CACHE_DURATION = 30000; // 30 seconds

  /**
   * Robust authentication check with auto-recovery
   * This will attempt to fix common auth issues automatically
   */
  static async ensureAuthenticatedWithRecovery(): Promise<string> {
    const now = Date.now();

    // Use cached result if recent and successful
    if (
      this.lastResult?.success &&
      this.lastResult.userId &&
      now - this.lastCheck < this.CACHE_DURATION
    ) {
      return this.lastResult.userId;
    }

    try {
      // Step 1: Try standard checks
      const standardResult = await this.standardAuthCheck();
      if (standardResult.success && standardResult.userId) {
        this.cacheResult(true, standardResult.userId);
        return standardResult.userId;
      }

      // Check if this is just a case of not being logged in (not an error)
      if (
        standardResult.error?.includes('No active session') ||
        standardResult.error?.includes('No user object returned')
      ) {
        // This is expected when not logged in - don't attempt recovery
        this.cacheResult(false, null);
        throw new Error('No active session - user needs to log in');
      }

      logger.warn('🚨 Standard auth check failed, attempting recovery...', standardResult.error);

      // Step 2: Debug the issue
      const debugInfo = await debugAuthentication();

      // Only attempt emergency fix if there's actually corrupted data
      if (
        debugInfo.checks.localStorage.tokenStructure === 'invalid' ||
        debugInfo.checks.localStorage.isExpired === true
      ) {
        // Step 3: Attempt emergency fix
        const fixResult = await emergencyAuthFix();

        if (fixResult.success) {
          // Step 4: Retry standard check after fix
          const retryResult = await this.standardAuthCheck();
          if (retryResult.success && retryResult.userId) {
            this.cacheResult(true, retryResult.userId);
            return retryResult.userId;
          }
        }
      }

      // Step 5: Last resort - try direct session refresh only if we have some session data
      if (debugInfo.checks.localStorage.hasToken) {
        const sessionResult = await this.forceSessionRefresh();
        if (sessionResult.success && sessionResult.userId) {
          this.cacheResult(true, sessionResult.userId);
          return sessionResult.userId;
        }
      }

      // If all recovery attempts fail (expired/invalid JWT, wrong project, or never signed in)
      this.cacheResult(false, null);
      throw new Error(
        'No valid Supabase session — sign in again, then retry (hard refresh if the app still shows you as logged in without working API calls).',
      );
    } catch (error) {
      this.cacheResult(false, null);
      logger.error('🚨 Critical auth failure:', error);
      throw error;
    }
  }

  /**
   * Standard authentication check (same as BaseService)
   */
  private static async standardAuthCheck(): Promise<{
    success: boolean;
    userId: string | null;
    error?: string;
  }> {
    try {
      // Check localStorage session
      // We log a warning but proceed to check Supabase SDK as true source of truth
      if (!hasActiveSession()) {
        logger.warn('AuthRecovery: No active session found in localStorage, but checking SDK...');
      }

      // Check Supabase API
      const {
        data: { user },
        error,
      } = await supabase.auth.getUser();

      if (error) {
        return { success: false, userId: null, error: `Supabase API error: ${error.message}` };
      }

      if (!user) {
        return { success: false, userId: null, error: 'No user object returned from Supabase' };
      }

      return { success: true, userId: user.id };
    } catch (error: any) {
      return { success: false, userId: null, error: `Exception: ${error.message}` };
    }
  }

  /**
   * Force refresh the session from Supabase
   */
  private static async forceSessionRefresh(): Promise<{
    success: boolean;
    userId: string | null;
    error?: string;
  }> {
    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.refreshSession();

      if (sessionError) {
        return {
          success: false,
          userId: null,
          error: `Session refresh failed: ${sessionError.message}`,
        };
      }

      if (!session?.user) {
        return { success: false, userId: null, error: 'No user in refreshed session' };
      }

      return { success: true, userId: session.user.id };
    } catch (error: any) {
      return { success: false, userId: null, error: `Session refresh exception: ${error.message}` };
    }
  }

  /**
   * Cache the authentication result
   */
  private static cacheResult(success: boolean, userId: string | null): void {
    this.lastCheck = Date.now();
    this.lastResult = { success, userId };
  }

  /**
   * Clear the authentication cache (useful after sign out)
   */
  static clearCache(): void {
    this.lastCheck = 0;
    this.lastResult = null;
  }

  /**
   * Invalidate the cache on successful auth events (SIGNED_IN, TOKEN_REFRESHED).
   * Fixes E2E flakiness where fresh browser contexts see stale failed-auth cache
   * for up to 30s after login completes.
   */
  static invalidate(): void {
    this.clearCache();
  }

  /**
   * Check if we have a cached successful auth result
   */
  static hasCachedAuth(): boolean {
    const now = Date.now();
    return !!(
      this.lastResult?.success &&
      this.lastResult.userId &&
      now - this.lastCheck < this.CACHE_DURATION
    );
  }
}

/**
 * Simple wrapper for backward compatibility
 */
export async function ensureAuthenticatedWithRecovery(): Promise<string> {
  return AuthRecovery.ensureAuthenticatedWithRecovery();
}
